import axios from "axios";
import Cookies from "universal-cookie";
import * as Sentry from "@sentry/react";
import { configureRetry, DEFAULT_TIMEOUT, REPORT_TIMEOUT } from "../utils/axiosRetryConfig.js";
import { clearAuthCookies, hasValidToken } from "../utils/authSession.js";
import { emitFrontendTelemetry } from "../utils/frontendTelemetry.js";
import logger from "../utils/logger.js";
import {
    isTemporaryApiFailure,
    notifyApiDegraded,
    notifyApiHealthy,
} from "../utils/apiAvailability.js";
import { normalizeSentryPath } from "../utils/sentryUtils.js";

// Normalize API URL by removing trailing slashes
const normalizeApiUrl = (url) => {
    return url ? url.replace(/\/+$/, '') : url;
};

// Create an axios instance for Node.js backend
const nodeAPI = axios.create({
    baseURL: normalizeApiUrl(process.env.VITE_API_URL || process.env.VITE_NODE_API_URL || 'http://localhost:3000'),
    withCredentials: true,
    timeout: DEFAULT_TIMEOUT,
});

// Retry must be configured BEFORE the response interceptor below so that
// intermediate retry attempts never trigger Sentry reports or login redirects.
configureRetry(nodeAPI);

const cookies = new Cookies();

const WIKI_AI_TIMEOUT = REPORT_TIMEOUT + 5000;
const isReportRequest = (url = "") => /\/report(\/|$)|\/reports(\/|$)/i.test(url);
const getRequestPath = (url = "") => {
    try {
        return new URL(String(url || ""), "http://hrp.local").pathname;
    } catch {
        return String(url || "").split("?")[0];
    }
};
const getNormalizedRequestPath = (url = "") => {
    const path = getRequestPath(url);
    return path.length > 1 ? path.replace(/\/+$/, "") : path;
};
const isArticleGenerationRequest = (url = "") => {
    const path = getNormalizedRequestPath(url);
    return (
        path === "/wiki/ai/write-article" ||
        path === "/wiki/ai/write-article-deep"
    );
};
const isWikiChatRequest = (url = "") => getNormalizedRequestPath(url) === "/wiki/ai/chat";
const isAspireSyncRequest = (url = "") =>
    getNormalizedRequestPath(url) === "/finance/aspire-invoice-allocation/pull";
const isAgencyRevenueRequest = (url = "") => {
    const path = getNormalizedRequestPath(url);
    return path === "/finance/agency-revenue" || path.startsWith("/finance/agency-revenue/");
};
const isDocumentImportDraftRequest = (url = "") =>
    /^\/wiki\/imports\/[^/]+\/drafts$/i.test(getNormalizedRequestPath(url));
const isLongRequest = (url = "") =>
    isReportRequest(url) ||
    isArticleGenerationRequest(url) ||
    isWikiChatRequest(url) ||
    isAspireSyncRequest(url) ||
    isAgencyRevenueRequest(url) ||
    isDocumentImportDraftRequest(url);
const getLongRequestTimeout = (url = "") =>
    isArticleGenerationRequest(url) || isWikiChatRequest(url) ? WIKI_AI_TIMEOUT : REPORT_TIMEOUT;
const isExportRequest = (url = "", config = {}) =>
    /download|export/i.test(url) || config.responseType === 'blob';

// Add auth token to all requests
nodeAPI.interceptors.request.use(function (config) {
    // Normalize double slashes in URL path (but not in protocol "://")
    if (config.url) {
        config.url = config.url.replace(/([^:]\/)\/+/g, '$1');
    }

    // Only use tokenR (standardized across the app)
    let token = cookies.get("tokenR");

    if (hasValidToken(token)) {
        // Add Bearer prefix if not already present
        if (!token.startsWith('Bearer ')) {
            token = `Bearer ${token}`;
        }
        config.headers.Authorization = token;
    } else if (token) {
        // Remove stale/expired session cookies to avoid repeated unauthorized retries.
        clearAuthCookies(cookies);
    }

    return config;
}, function (error) {
    return Promise.reject(error);
});

// Request interceptor: auto-apply long-request timeouts when callers still use the default.
// Reports match the backend budget; Wiki AI waits slightly longer so the API response wins the race.
// Components may still pass an explicit timeout to override this (e.g. POLL_TIMEOUT for lightweight status checks).
nodeAPI.interceptors.request.use((config) => {
    if (isLongRequest(config.url) && config.timeout === DEFAULT_TIMEOUT) {
        config.timeout = getLongRequestTimeout(config.url);
    }
    // These operations keep processing on the server after a client timeout.
    // Retrying would start overlapping work, including duplicate draft creation.
    if (
        isArticleGenerationRequest(config.url) ||
        isWikiChatRequest(config.url) ||
        isAspireSyncRequest(config.url) ||
        isDocumentImportDraftRequest(config.url)
    ) {
        config["axios-retry"] = {
            ...(config["axios-retry"] || {}),
            retries: 0,
        };
    }
    return config;
});

// Handle response errors
nodeAPI.interceptors.response.use(
    (response) => {
        notifyApiHealthy({ source: "api_response" });
        return response;
    },
    (error) => {
        const method = error.config?.method?.toUpperCase();
        const url = error.config?.url;
        const requestId = error.response?.headers?.["x-request-id"] || error.response?.headers?.["X-Request-Id"];
        const retryCount = Number.isInteger(error.config?.["axios-retry"]?.retryCount)
            ? error.config["axios-retry"].retryCount
            : null;

        if (isTemporaryApiFailure(error)) {
            notifyApiDegraded("api_request_failed", {
                source: "api_response",
                statusCode: error.response?.status || null,
                method,
                url,
                code: error.code || null,
            });
        }

        if (error.response) {
            const status = error.response.status;

            // Handle maintenance mode (503)
            if (status === 503 && error.response.data?.status === 'MAINTENANCE') {
                // Redirect to maintenance page
                if (window.location.pathname !== '/maintenance') {
                    window.location.href = '/maintenance';
                }
            }
            // Handle unauthorized (401)
            else if (status === 401) {
                notifyApiHealthy({ source: "auth_response", statusCode: 401 });

                void emitFrontendTelemetry("frontend.auth.failure", {
                    area: "auth",
                    action: "unauthorized_response",
                    method,
                    url,
                    statusCode: status,
                    requestId,
                    retryCount,
                    errorCode: error.code,
                    message: error.message,
                });
                clearAuthCookies(cookies);

                // Don't redirect if we're already on the login page
                const currentPath = window.location.pathname;
                if (currentPath !== '/login') {
                    // Token expired or invalid - redirect to login
                    window.location.href = '/login';
                }
            }

            // Sentry: breadcrumb for 4xx (except 401), captureException for 5xx (except 503)
            if (status >= 400 && status < 500 && status !== 401) {
                Sentry.addBreadcrumb({
                    category: "api.error",
                    message: `${error.config?.method?.toUpperCase()} ${error.config?.url} → ${status}`,
                    level: "warning",
                    data: { api: "node", status },
                });
                if (status === 403) {
                    void emitFrontendTelemetry("frontend.api.permission_denied", {
                        area: "api",
                        action: "forbidden_response",
                        method,
                        url,
                        statusCode: status,
                        requestId,
                        retryCount,
                        errorCode: error.code,
                        message: error.message,
                    });
                }
            } else if (status >= 500 && status !== 503) {
                void emitFrontendTelemetry("frontend.api.failure", {
                    area: "api",
                    action: "request_failed",
                    method,
                    url,
                    statusCode: status,
                    requestId,
                    retryCount,
                    errorCode: error.code,
                    message: error.message,
                });
                if (isReportRequest(url)) {
                    logger.reportFailure({
                        area: 'report',
                        action: 'request_failed',
                        url,
                        method,
                    }, error);
                }
                if (isExportRequest(url, error.config || {})) {
                    logger.exportFailure({
                        area: 'export',
                        action: 'request_failed',
                        url,
                        method,
                    }, error);
                }
                const normalizedPath5xx = normalizeSentryPath(error.config?.url);
                // Backend 5xx errors are captured by backend Sentry with full server context.
                // Record as a breadcrumb (visible in other error traces) rather than a
                // duplicate top-level event with a minified frontend stack trace.
                Sentry.addBreadcrumb({
                    category: "api.error",
                    message: `${method} ${url} → ${status}`,
                    level: "error",
                    data: { api: "node", status, requestId, url_path: normalizedPath5xx },
                });
            }
        } else if (error.code !== 'ERR_CANCELED') {
            void emitFrontendTelemetry("frontend.api.failure", {
                area: "api",
                action: "network_failure",
                method,
                url,
                retryCount,
                errorCode: error.code,
                message: error.message,
            });
            if (isReportRequest(url)) {
                logger.reportFailure({
                    area: 'report',
                    action: 'network_failure',
                    url,
                    method,
                }, error);
            }
            if (isExportRequest(url, error.config || {})) {
                logger.exportFailure({
                    area: 'export',
                    action: 'network_failure',
                    url,
                    method,
                }, error);
            }
            // Capture to Sentry so timeout/network failures are visible alongside 5xx errors.
            // Mirrors the captureException call in the error.response branch above.
            const normalizedPathNetwork = normalizeSentryPath(error.config?.url);
            Sentry.captureException(error, {
                tags: { api: "node", url_path: normalizedPathNetwork, http_method: method },
                fingerprint: ['axios-network', normalizedPathNetwork, error.code || 'unknown'],
                contexts: {
                    network: {
                        url: error.config?.url,
                        method: error.config?.method?.toUpperCase(),
                        errorCode: error.code,
                    },
                },
            });
        }
        return Promise.reject(error);
    }
);

export default nodeAPI;
